import React, { useEffect, useState } from 'react';
import {
  Megaphone, Users, QrCode, Link2, Mail, Image as ImageIcon, Film, FileText,
  CheckCircle2, ListTodo, TrendingUp, DollarSign, Sparkles, Clock, ArrowRight, Zap
} from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Panel, PanelLoader, Card, Stat } from './shared';
import { toast } from 'sonner';

const QUICK_ACTIONS = [
  { label: 'New Campaign', desc: 'Brief in, full plan out', nav: 'campaigns', icon: Megaphone },
  { label: 'Design Something', desc: 'Canva-style studio', nav: 'design', icon: ImageIcon },
  { label: 'Sora 2 Video Ad', desc: 'Generate a video ad', nav: 'video-ads', icon: Film },
  { label: 'QR Code', desc: 'Trackable print codes', nav: 'qr', icon: QrCode },
  { label: 'Email Blast', desc: 'Build, test and send', nav: 'email', icon: Mail },
  { label: 'Landing Page', desc: 'Capture leads today', nav: 'pages', icon: FileText },
];

export default function CommandCenter({ setActiveSection }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    suiteAPI.commandCenter().then(r => setData(r.data)).catch(() => toast.error('Could not load command center'));
  }, []);

  if (!data) return <PanelLoader />;
  const { totals, counts } = data;

  const money = (n) => `$${Number(n || 0).toLocaleString()}`;

  return (
    <Panel
      title="Command Center"
      subtitle="Every number, task and approval across the whole marketing operation."
      testId="suite-command-center"
      action={
        <Button onClick={() => setActiveSection('analytics')} className="bg-[#b8ff00] text-black font-bold" data-testid="cc-analytics-btn">
          <TrendingUp size={15} className="mr-1.5" /> Full Analytics
        </Button>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Stat label="Spend" value={money(totals.spend)} sub={`${counts.active_campaigns} active campaigns`} />
        <Stat label="Revenue" value={money(totals.revenue)} tone="text-[#b8ff00]" sub={`ROAS ${totals.roas}x`} />
        <Stat label="Clicks & Scans" value={Number(totals.total_clicks).toLocaleString()} sub={`${totals.qr_scans} QR scans`} />
        <Stat label="Leads" value={counts.leads} tone="text-cyan-400" sub={`${counts.new_leads} new`} />
        <Stat label="Audience" value={counts.contacts} sub="subscribed contacts" />
        <Stat label="Open Tasks" value={counts.open_tasks} tone={counts.overdue_tasks ? 'text-amber-400' : 'text-white'} sub={`${counts.overdue_tasks} overdue`} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {QUICK_ACTIONS.map(a => (
          <button
            key={a.label}
            onClick={() => setActiveSection(a.nav)}
            data-testid={`quick-${a.nav}`}
            className="group bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-left hover:border-[#b8ff00]/50 transition-colors"
          >
            <a.icon size={18} className="text-[#b8ff00] mb-2" />
            <p className="text-sm font-semibold text-white flex items-center gap-1">
              {a.label}<ArrowRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
            <p className="text-[11px] text-zinc-500">{a.desc}</p>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Clock size={14} className="text-cyan-400" /> Next 7 Days</h3>
          {data.upcoming_calendar.length === 0 ? (
            <p className="text-xs text-zinc-500">Nothing scheduled. Generate a campaign plan to auto-fill your calendar.</p>
          ) : data.upcoming_calendar.map(c => (
            <div key={c.id} className="flex items-center gap-3 py-2 border-b border-zinc-800 last:border-0" data-testid={`cc-cal-${c.id}`}>
              <span className="text-[10px] font-mono text-[#b8ff00] w-16 shrink-0">{c.date?.slice(5)}</span>
              <div className="min-w-0">
                <p className="text-xs text-white truncate">{c.title}</p>
                <p className="text-[10px] text-zinc-500 capitalize">{c.channel} · {c.content_type}</p>
              </div>
            </div>
          ))}
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><ListTodo size={14} className="text-violet-400" /> Task Queue</h3>
          {data.my_tasks.length === 0 ? <p className="text-xs text-zinc-500">No open tasks.</p> : data.my_tasks.map(t => (
            <button key={t.id} onClick={() => setActiveSection('campaigns')} className="w-full text-left py-2 border-b border-zinc-800 last:border-0" data-testid={`cc-task-${t.id}`}>
              <p className="text-xs text-white truncate">{t.title}</p>
              <p className="text-[10px] text-zinc-500 capitalize">{t.status.replace('_', ' ')} · {t.priority}</p>
            </button>
          ))}
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Users size={14} className="text-[#b8ff00]" /> Latest Leads</h3>
          {data.recent_leads.length === 0 ? <p className="text-xs text-zinc-500">No leads yet. Publish a landing page to start capturing.</p> : data.recent_leads.map(l => (
            <div key={l.id} className="py-2 border-b border-zinc-800 last:border-0" data-testid={`cc-lead-${l.id}`}>
              <p className="text-xs text-white truncate">{l.name || l.email || 'Anonymous'}</p>
              <p className="text-[10px] text-zinc-500 truncate">{l.source}</p>
            </div>
          ))}
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <Card className="p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-400" /> Waiting on Approval ({counts.pending_approvals})</h3>
          {data.pending_approvals.length === 0 ? <p className="text-xs text-zinc-500">Nothing pending review.</p> : data.pending_approvals.map(a => (
            <button key={a.id} onClick={() => setActiveSection('approvals')} className="w-full text-left py-2 border-b border-zinc-800 last:border-0">
              <p className="text-xs text-white truncate">{a.item_name}</p>
              <p className="text-[10px] text-zinc-500 capitalize">{a.item_type}</p>
            </button>
          ))}
        </Card>
        <Card className="p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Zap size={14} className="text-zinc-400" /> Team Activity</h3>
          {data.activity.length === 0 ? <p className="text-xs text-zinc-500">No activity yet.</p> : data.activity.map(a => (
            <div key={a.id} className="py-1.5 border-b border-zinc-800 last:border-0 flex items-center gap-2">
              <Sparkles size={11} className="text-[#b8ff00] shrink-0" />
              <p className="text-[11px] text-zinc-400 truncate">
                <span className="text-white">{a.action?.replace(/_/g, ' ')}</span> — {a.entity}
              </p>
            </div>
          ))}
        </Card>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-4">
        {[
          { l: 'Designs', v: counts.designs, nav: 'design', icon: ImageIcon },
          { l: 'QR Codes', v: counts.qr_codes, nav: 'qr', icon: QrCode },
          { l: 'Links', v: counts.links, nav: 'links', icon: Link2 },
          { l: 'Emails', v: counts.emails, nav: 'email', icon: Mail },
          { l: 'Pages', v: counts.published_pages, nav: 'pages', icon: FileText },
          { l: 'Video Ads', v: counts.video_ads, nav: 'video-ads', icon: Film },
        ].map(x => (
          <button key={x.l} onClick={() => setActiveSection(x.nav)} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-left hover:border-zinc-700" data-testid={`cc-count-${x.nav}`}>
            <x.icon size={14} className="text-zinc-500 mb-1" />
            <p className="text-lg font-bold text-white leading-none">{x.v}</p>
            <p className="text-[10px] text-zinc-500">{x.l}</p>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-zinc-600 mt-6 flex items-center gap-1.5">
        <DollarSign size={12} /> CPL {money(totals.cpl)} · CPC {money(totals.cpc)} · Email open rate {totals.email_open_rate}%
      </p>
    </Panel>
  );
}
